import type { Question } from '../../types';
import { HAB_MH_CONDITION } from './shared-options';

// ---------------------------------------------------------------------------
// General Liability — Habitational Supplement
// Source: Centered Partners MHMF intake — "General Liability" sheet.
// Gated on GL/BOP selected + habitational or manufactured housing industry.
// ---------------------------------------------------------------------------

const GL_HAB_CONDITION = [
  { field: 'coverage_lines', operator: 'contains' as const, value: 'gl_bop' },
  ...HAB_MH_CONDITION,
];

export const glHabitationalQuestions: Question[] = [
  // Pools & hot tubs ---------------------------------------------------
  {
    id: 'glh_has_pool',
    text: 'Do any properties have a swimming pool or hot tub?',
    section: 'operations',
    group: 'glh_pool',
    sort_order: 500,
    field_type: 'boolean',
    required: true,
    field_path: 'gl.habitational.has_pool',
    conditions: GL_HAB_CONDITION,
    tags: ['gl', 'habitational'],
  },
  {
    id: 'glh_pool_count',
    text: 'How many pools / hot tubs across the portfolio?',
    section: 'operations',
    group: 'glh_pool',
    sort_order: 510,
    field_type: 'number',
    placeholder: '2',
    required: true,
    field_path: 'gl.habitational.pool_count',
    conditions: [...GL_HAB_CONDITION, { field: 'glh_has_pool', operator: 'eq', value: true }],
    tags: ['gl', 'habitational'],
  },
  {
    id: 'glh_pool_diving_board',
    text: 'Do any pools have a diving board or slide?',
    section: 'operations',
    group: 'glh_pool',
    sort_order: 520,
    field_type: 'boolean',
    required: true,
    field_path: 'gl.habitational.pool_diving_board',
    conditions: [...GL_HAB_CONDITION, { field: 'glh_has_pool', operator: 'eq', value: true }],
    tags: ['gl', 'habitational'],
  },
  {
    id: 'glh_pool_fenced',
    text: 'Are all pools fenced with self-latching gates?',
    section: 'operations',
    group: 'glh_pool',
    sort_order: 530,
    field_type: 'boolean',
    required: true,
    field_path: 'gl.habitational.pool_fenced',
    conditions: [...GL_HAB_CONDITION, { field: 'glh_has_pool', operator: 'eq', value: true }],
    tags: ['gl', 'habitational'],
  },
  {
    id: 'glh_pool_cert',
    text: 'Is there a certified pool operator?',
    subtext: 'CPO certification or equivalent.',
    section: 'operations',
    group: 'glh_pool',
    sort_order: 540,
    field_type: 'boolean',
    required: true,
    field_path: 'gl.habitational.pool_operator_cert',
    conditions: [...GL_HAB_CONDITION, { field: 'glh_has_pool', operator: 'eq', value: true }],
    tags: ['gl', 'habitational'],
  },

  // Playgrounds ----------------------------------------------------------
  {
    id: 'glh_has_playground',
    text: 'Do any properties have playgrounds or amenity equipment?',
    section: 'operations',
    group: 'glh_playground',
    sort_order: 550,
    field_type: 'boolean',
    required: true,
    field_path: 'gl.habitational.has_playground',
    conditions: GL_HAB_CONDITION,
    tags: ['gl', 'habitational'],
  },
  {
    id: 'glh_playground_detail',
    text: 'Describe the playground / amenity equipment',
    subtext: 'Equipment type, year installed, date of last safety audit.',
    section: 'operations',
    group: 'glh_playground',
    sort_order: 560,
    field_type: 'textarea',
    placeholder: 'Playset installed 2018, fall-zone mulch, last audited March 2026.',
    required: true,
    field_path: 'gl.habitational.playground_detail',
    conditions: [...GL_HAB_CONDITION, { field: 'glh_has_playground', operator: 'eq', value: true }],
    tags: ['gl', 'habitational'],
  },

  // Dogs / pets ----------------------------------------------------------
  {
    id: 'glh_dogs_allowed',
    text: 'Are dogs allowed on the premises?',
    section: 'operations',
    group: 'glh_dogs',
    sort_order: 570,
    field_type: 'boolean',
    required: true,
    field_path: 'gl.habitational.dogs_allowed',
    conditions: GL_HAB_CONDITION,
    tags: ['gl', 'habitational'],
  },
  {
    id: 'glh_breed_restrictions',
    text: 'Is an aggressive-breed restriction list enforced?',
    section: 'operations',
    group: 'glh_dogs',
    sort_order: 580,
    field_type: 'boolean',
    required: true,
    field_path: 'gl.habitational.breed_restrictions',
    conditions: [...GL_HAB_CONDITION, { field: 'glh_dogs_allowed', operator: 'eq', value: true }],
    tags: ['gl', 'habitational'],
  },
  {
    id: 'glh_bite_incidents',
    text: 'Any prior dog bite incidents?',
    section: 'operations',
    group: 'glh_dogs',
    sort_order: 590,
    field_type: 'boolean',
    required: true,
    field_path: 'gl.habitational.bite_incidents',
    conditions: [...GL_HAB_CONDITION, { field: 'glh_dogs_allowed', operator: 'eq', value: true }],
    tags: ['gl', 'habitational'],
  },
  {
    id: 'glh_bite_detail',
    text: 'Describe the dog bite incidents',
    subtext: 'Dates, severity, and whether claims were filed.',
    section: 'operations',
    group: 'glh_dogs',
    sort_order: 600,
    field_type: 'textarea',
    required: true,
    field_path: 'gl.habitational.bite_detail',
    conditions: [...GL_HAB_CONDITION, { field: 'glh_bite_incidents', operator: 'eq', value: true }],
    tags: ['gl', 'habitational'],
  },

  // Contractual requirements ----------------------------------------------
  {
    id: 'glh_ai_requirements',
    text: 'Any additional insured or waiver of subrogation requirements?',
    subtext: 'From lenders, property managers, or affiliated entities.',
    section: 'operations',
    group: 'glh_contractual',
    sort_order: 610,
    field_type: 'boolean',
    required: true,
    field_path: 'gl.habitational.ai_requirements',
    conditions: GL_HAB_CONDITION,
    tags: ['gl', 'habitational'],
  },
  {
    id: 'glh_ai_detail',
    text: 'Describe the additional insured / waiver requirements',
    section: 'operations',
    group: 'glh_contractual',
    sort_order: 620,
    field_type: 'textarea',
    placeholder: 'Lender requires AI + WoS on GL and Umbrella; manager requires AI.',
    required: true,
    field_path: 'gl.habitational.ai_detail',
    conditions: [
      ...GL_HAB_CONDITION,
      { field: 'glh_ai_requirements', operator: 'eq', value: true },
    ],
    tags: ['gl', 'habitational'],
  },
];
